import React, { useState } from 'react';
import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import OnboardingVideoCard from 'components/shared/OnboardingVideoCard';

const formatCreditsDisplay = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  return Number(value).toLocaleString('fr-FR');
};

export const TextDetailsIA = ({
  t,
  // expanded et setExpanded ne sont plus utilisés,
  // la section reste toujours ouverte visuellement
  formData,
  errors,
  isFieldDisabled,
  handleChange,
  handleBlur,
  websitesList = [],
  selectedWebsite,
  setSelectedWebsite,
  onRegenerateTitle,
  onRegenerateMeta,
  isRegeneratingTitle = false,
  isRegeneratingMeta = false,
  onStartGeneration,
  isGenerating = false,
  hasContentOrGenerated = false,
  showSeoControls = true,
  showUrlControls = true,
  onOpenGenerationSettings = () => { },
  isCreditsExhausted = false,
  onCreditsLimitClick = () => { },
  remainingCredits = null,
  sidebarCollapsed = false,
  hasScore = true,
  selectedAgent = null,
  aiAgentModel = '',
  agentName = '',
  expandMetaField = false,
  showCreditsBadge = true,
  actionsInlineWithTitle = false,
  showGenerationActions = true,
  isTranslationEnabled = false,
  onOpenTranslationPopin,
  showContentScoreVideo = false,
}) => {
  const { keyword, url, titleTag, metaDescriptionTag } = formData || {};
  const { titleError, metaDescriptionError } = errors || {};

  const [showSeoTags, setShowSeoTags] = useState(false);
  const [showUrlSection, setShowUrlSection] = useState(false);
  const [isEditingKeyword, setIsEditingKeyword] = useState(false);
  const [keywordDraft, setKeywordDraft] = useState(keyword || '');

  const TITLE_MAX = 120;
  const metaCharLimit = expandMetaField ? null : 120;

  const titleLength = titleTag?.length || 0;
  const metaLength = metaDescriptionTag?.length || 0;

  const agentModelName = aiAgentModel || '';

  const agentModelDetails =
    selectedAgent?.aiAgentModel?.fullDetails ||
    selectedAgent?.aiAgentModel?.description ||
    '';

  const startKeywordEdit = () => {
    setKeywordDraft(keyword || '');
    setIsEditingKeyword(true);
  };

  const confirmKeywordEdit = () => {
    if (isFieldDisabled('keyword')) {
      setIsEditingKeyword(false);
      return;
    }

    if (typeof handleChange === 'function') {
      const onChange = handleChange('keyword');
      if (typeof onChange === 'function') {
        onChange({ target: { value: keywordDraft || '' } });
      }
    }

    setIsEditingKeyword(false);
  };

  const renderCreditsBadge = () => {
    if (!showCreditsBadge || remainingCredits === null) return null;

    return (
      <div className="textdetails-credits-badge" aria-label={t('Crédits IA')}>
        <span className="textdetails-credits-badge-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
          </svg>
        </span>
        <span className="textdetails-credits-badge-text">
          {formatCreditsDisplay(remainingCredits)} {t('Crédits IA')}
        </span>
      </div>
    );
  };

  const renderContentScoreVideo = () => {
    if (!showContentScoreVideo) return null;

    return (
      <OnboardingVideoCard
        compact
        dismissible={false}
        enablePlaylist={false}
        videoSrc="/Score-de-contenu.mp4"
        titleKey="Score de contenu"
        className="textdetails-content-score-video"
      />
    );
  };

  const renderGenerationActions = () => {
    if (!onStartGeneration && !hasContentOrGenerated) return null;

    return (
      <>
        {hasContentOrGenerated && showGenerationActions && isTranslationEnabled && onOpenTranslationPopin && (
          <>
            <button
              type="button"
              className="textdetails-traduire"
              onClick={onOpenTranslationPopin}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <img
                  src="/Images/Typix_Traduction-selection.png"
                  alt={t('Traduire')}
                  style={{ width: 16, height: 16 }}
                />
                {t('Traduire')}
              </span>
            </button>
            {onStartGeneration && <span className="textdetails-actions-separator" />}
          </>
        )}
        {onStartGeneration && showGenerationActions && (
          <>
            <button
              type="button"
              className="textdetails-relancer"
              onClick={() => {
                if (isCreditsExhausted) {
                  onCreditsLimitClick?.();
                  return;
                }
                onStartGeneration?.();
              }}
              disabled={isGenerating}
            >
              {isGenerating ? (
                t('Génération en cours...')
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <img
                    src="/images/icon-relan-ia.png"
                    alt="Relancer IA"
                    style={{ width: 16, height: 16 }}
                  />
                  {hasContentOrGenerated ? t('Relancer l’IA') : t('Lancer')}
                </span>
              )}
            </button>
            <span className="textdetails-actions-separator" />
            <button
              type="button"
              className="textdetails-params"
              onClick={isCreditsExhausted ? onCreditsLimitClick : onOpenGenerationSettings}
            >
              <img
                src="/Images/set-icon.png"
                alt={t('Paramètres de génération')}
                className="textdetails-params-icon"
              />
              {t('Paramètres de génération')}
            </button>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <style>{`
        .textdetails-display {
          position: relative;
          max-width: 2500px;
        }
        .textdetails-content-score-video {
          position: absolute;
          top: 5px;
          right: 5px;
          z-index: 5;
        }
        .textdetails-title {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 20px;
          font-weight: 600;
          color: #000;
          margin-bottom: 4px;
          margin-left: 20px;
          cursor: pointer;
        }
        .textdetails-title-text {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .textdetails-title-edit-icon {
          font-size: 13px;
          color: #9CA3AF;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .textdetails-title:hover .textdetails-title-edit-icon {
          opacity: 1;
        }
        .textdetails-badge {
          display: inline-flex;
          align-items: center;
          background-color: #e8d4ff;
          color: #7c3aed;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 500;
          margin-left: 10px;
        }
        .textdetails-title-edit {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-left: 20px;
          margin-bottom: 4px;
        }
        .textdetails-title-input {
          padding: 8px 10px;
          border-radius: 6px;
          border: 1px solid #D1D5DB;
          font-size: 14px;
          min-width: 800px;
        }
        .textdetails-title-input:focus {
          outline: none;
          border-color: #7c3aed;
        }
        .textdetails-title-check {
          border: none;
          background-color: transparent;
          color: #111827;
          border-radius: 0;
          padding: 0 4px;
          font-size: 16px;
          cursor: pointer;
        }
        .textdetails-info {
          color: #666;
          font-size: 14px;
          margin-bottom: 15px;
          line-height: 1.6;
          margin-left: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .textdetails-buttons {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-left: 20px;
          gap: 10px;
        }
        .textdetails-buttons-left {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .textdetails-buttons-right {
          margin-left: auto;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .textdetails-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 16px;
          background-color: #F1F1F1;
          border: 1px solid #EEEEEE;
          border-radius: 50px;
          font-size: 14px;
          color: #333;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(228, 227, 227, 0.85);
          margin-bottom: 10px;
          margin-top: 10px;
        }
        .textdetails-btn svg {
          width: 16px;
          height: 16px;
        }
        .textdetails-modal {
          margin-top: 15px;
          margin-left: 20px;
          margin-right: 20px;
          padding: 0;
          background-color: transparent;
          border: none;
          box-shadow: none;
          margin-bottom: 10px;
        }
        .textdetails-field {
          margin-bottom: 18px;
          padding: 16px 18px;
          background: #FAFAFA;
          border-radius: 8px;
          border: 1px solid #E9E9E9;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          margin-bottom: 10px;
        }
        .textdetails-field:last-child {
          margin-bottom: 0;
        }
        .textdetails-field label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 600;
          font-size: 14px;
          color: #333;
          margin-bottom: 8px;
          gap: 8px;
        }
        .textdetails-field input,
        .textdetails-field textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          color: #333;
          transition: border-color 0.2s;
          background-color: white;
        }
        .textdetails-field input:focus,
        .textdetails-field textarea:focus {
          outline: none;
          border-color: #7c3aed;
        }
        .textdetails-field textarea {
          resize: vertical;
          min-height: 80px;
        }
        .textdetails-charcount {
          margin-top: 6px;
          font-size: 12px;
          font-weight: 500;
          color: #16a34a;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .textdetails-charcount-badge {
          padding: 2px 8px;
          border-radius: 999px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
        }
        .textdetails-credits-badge {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 5px 11px;
          border-radius: 999px;
          background-color:rgb(183, 238, 212);
          border: 1px solid #bbf7d0;
          flex-shrink: 0;
          margin-left: 10px;
        }
        .textdetails-credits-badge-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 0;
          color: #ca8a04;
        }
        .textdetails-credits-badge-icon svg {
          display: block;
          width: 18px;
          height: 18px;
        }
        .textdetails-credits-badge-text {
          font-size: 12px;
          font-weight: 600;
          color: #166534;
          white-space: nowrap;
        }
        .textdetails-relancer {
          border: none;
          background-color: transparent;
          color: #5E45E6;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
        }
        .textdetails-traduire {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: none;
          border-radius: 999px;
          background-color: #EDE9FE;
          color: #7C3AED;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          flex-shrink: 0;
        }
        .textdetails-traduire:hover:not(:disabled) {
          background-color: #DDD6FE;
        }
        .textdetails-traduire:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .textdetails-actions-separator {
          width: 1px;
          height: 18px;
          background-color: #E5E7EB;
          margin-top:5px;
        }
        .textdetails-params {
          border: none;
          background-color: transparent;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #111827;
          font-weight: 500;
          cursor: pointer;
          margin-right:'10px';
        }
        .textdetails-params-icon {
          width: 16px;
          height: 16px;
        }
        .textdetails-input-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .textdetails-input-row input,
        .textdetails-input-row textarea {
          flex: 1;
        }
        .textdetails-input-row-meta {
          align-items: flex-start;
        }
        .textdetails-field-meta textarea {
          min-height: 100px;
          resize: vertical;
        }
        .textdetails-regen-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 999px;
          border: 1px solid #00000029;
          background:#FFFFFF;
          font-size: 13px;
          font-weight: 500;
          color: #111827;
          white-space: nowrap;
          cursor: pointer;
        }
        .textdetails-regen-btn:hover:not(:disabled) {
          background: #EEF2FF;
          border-color: #C7D2FE;
          color: #4F46E5;
        }
        .textdetails-regen-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .textdetails-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 20px;
          margin-bottom: 15px;
          margin-left: 20px;
          margin-right: 20px;
        }
        .textdetails-title-row-left {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          min-width: 0;
        }
        .textdetails-title-row-right {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
          margin-left: auto;
        }
      `}</style>

      <div className="row">
        <div className="col-12">
          <div className="box-wrapper-editor textdetails-display">
            {renderContentScoreVideo()}

            {actionsInlineWithTitle ? (
              <div className="textdetails-title-row">
                <div className="textdetails-title-row-left">
                  {isEditingKeyword ? (
                    <>
                      <input
                        type="text"
                        className="textdetails-title-input"
                        value={keywordDraft}
                        onChange={(e) => setKeywordDraft(e.target.value)}
                        placeholder={t('Entrez le mot-clé principal')}
                        disabled={isFieldDisabled('keyword')}
                        style={{ minWidth: 0, flex: '1 1 280px' }}
                      />
                      <button
                        type="button"
                        className="textdetails-title-check"
                        onClick={confirmKeywordEdit}
                        disabled={isFieldDisabled('keyword')}
                      >
                        ✓
                      </button>
                    </>
                  ) : (
                    <span
                      className="textdetails-title"
                      style={{ marginLeft: 0, marginBottom: 0 }}
                      onClick={!isFieldDisabled('keyword') ? startKeywordEdit : undefined}
                      title={t('Modifier le mot-clé principal')}
                    >
                      <span className="textdetails-title-text">
                        {keyword || t('Détails du texte')}
                      </span>
                      {!isFieldDisabled('keyword') && (
                        <span className="textdetails-title-edit-icon">✎</span>
                      )}
                    </span>
                  )}
                  <span className="textdetails-badge" style={{ marginLeft: 0 }}>
                    <img
                      src="/Images/gen-icon-ai.png"
                      alt="AI"
                      style={{ width: 16, height: 16, marginRight: 6 }}
                    />
                    {t('Généré par IA')}
                  </span>
                  {renderCreditsBadge()}
                </div>
                <div className="textdetails-title-row-right">
                  {renderGenerationActions()}
                </div>
              </div>
            ) : isEditingKeyword ? (
              <div className="textdetails-title-edit" style={{ marginTop: 20, marginBottom: '15px' }}>
                <input
                  type="text"
                  className="textdetails-title-input"
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  placeholder={t('Entrez le mot-clé principal')}
                  disabled={isFieldDisabled('keyword')}
                />
                <button
                  type="button"
                  className="textdetails-title-check"
                  onClick={confirmKeywordEdit}
                  disabled={isFieldDisabled('keyword')}
                >
                  ✓
                </button>
                <span className="textdetails-badge">
                  <img
                    src="/Images/gen-icon-ai.png"
                    alt="AI"
                    style={{ width: 16, height: 16, marginRight: 6 }}
                  />
                  {t('Généré par IA')}
                </span>
                {renderCreditsBadge()}
              </div>
            ) : (
              <div style={{ marginBottom: '15px', marginTop: 20 }}>
                <span
                  className="textdetails-title"
                  onClick={!isFieldDisabled('keyword') ? startKeywordEdit : undefined}
                  title={t('modifier le mot-clé principal')}
                >
                  <span className="textdetails-title-text">
                    {keyword || t('Détails du texte')}
                  </span>
                  {!isFieldDisabled('keyword') && (
                    <span className="textdetails-title-edit-icon">✎</span>
                  )}
                </span>
                <span className="textdetails-badge">
                  <img
                    src="/Images/gen-icon-ai.png"
                    alt="AI"
                    style={{ width: 16, height: 16, marginRight: 6 }}
                  />
                  {t('Généré par IA')}
                </span>
                {renderCreditsBadge()}
              </div>
            )}

            <>
              {/* Infos rapides (mot-clé & URL) */}
              <div className="textdetails-info">
                <span>{t('Mot clé')} : {keyword || t('Non défini')}</span>
                {url && (
                  <span>{t('URL')} : {url}</span>
                )}
                {agentModelName && (
                  <span>{t('Modèle IA')} : {agentModelName}</span>
                )}
                {agentName && (
                  <span>{t('Agent IA')} : {agentName}</span>
                )}
                {agentModelDetails && (
                  <span>{agentModelDetails}</span>
                )}
              </div>


              {(showSeoControls || showUrlControls || onStartGeneration || hasContentOrGenerated) && (
              <div className="textdetails-buttons">
                <div className="textdetails-buttons-left">
                  {showSeoControls && (
                    <button
                      type="button"
                      className="textdetails-btn"
                      onClick={() => setShowSeoTags(!showSeoTags)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {t('Balise Title & Meta')}
                    </button>
                  )}
                  {showUrlControls && (
                    <button
                      type="button"
                      className="textdetails-btn"
                      onClick={() => setShowUrlSection(!showUrlSection)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {t('URL')}
                    </button>
                  )}
                </div>
                {!actionsInlineWithTitle && (onStartGeneration || hasContentOrGenerated) && (
                  <div className="textdetails-buttons-right">
                    {renderGenerationActions()}
                  </div>
                )}
              </div>
              )}

              {showSeoControls && showSeoTags && (
                <div className="textdetails-modal">
                  <div className="textdetails-field">
                    <label htmlFor="titleTagInput">
                      {t('Balise Title :')}
                    </label>
                    <div className="textdetails-input-row">
                      <input
                        id="titleTagInput"
                        name="titleTag"
                        type="text"
                        value={titleTag || ''}
                        maxLength={TITLE_MAX}
                        onChange={!isFieldDisabled('titleTag') ? handleChange('titleTag') : undefined}
                        onBlur={handleBlur('titleTag')}
                        placeholder={t('Entrez la balise title')}
                        disabled={isFieldDisabled('titleTag')}
                      />
                      {onRegenerateTitle && (
                        <button
                          type="button"
                          className="textdetails-regen-btn"
                          onClick={isCreditsExhausted ? onCreditsLimitClick : onRegenerateTitle}
                          disabled={isRegeneratingTitle}
                          title={t('Régénérer avec l’IA')}
                        >
                          ⟳ {t('Régénérer avec l’IA')}
                        </button>
                      )}
                    </div>
                    <div className="textdetails-charcount">
                      <span className="textdetails-charcount-badge">
                        {titleLength} / {TITLE_MAX} {t('caractères')}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`textdetails-field${
                      expandMetaField ? ' textdetails-field-meta' : ''
                    }`}
                  >
                    <label htmlFor="metaTagInput">
                      {t('Balise Meta Description :')}
                    </label>
                    <div
                      className={`textdetails-input-row${
                        expandMetaField ? ' textdetails-input-row-meta' : ''
                      }`}
                    >
                      {expandMetaField ? (
                        <textarea
                          id="metaTagInput"
                          name="metaDescriptionTag"
                          rows={4}
                          value={metaDescriptionTag || ''}
                          onChange={!isFieldDisabled('metaDescriptionTag') ? handleChange('metaDescriptionTag') : undefined}
                          onBlur={handleBlur('metaDescriptionTag')}
                          placeholder={t('Entrez la meta description')}
                          disabled={isFieldDisabled('metaDescriptionTag')}
                        />
                      ) : (
                        <input
                          id="metaTagInput"
                          name="metaDescriptionTag"
                          type="text"
                          value={metaDescriptionTag || ''}
                          maxLength={metaCharLimit}
                          onChange={!isFieldDisabled('metaDescriptionTag') ? handleChange('metaDescriptionTag') : undefined}
                          onBlur={handleBlur('metaDescriptionTag')}
                          placeholder={t('Entrez la meta description')}
                          disabled={isFieldDisabled('metaDescriptionTag')}
                        />
                      )}
                      {onRegenerateMeta && (
                        <button
                          type="button"
                          className="textdetails-regen-btn"
                          onClick={isCreditsExhausted ? onCreditsLimitClick : onRegenerateMeta}
                          disabled={isRegeneratingMeta}
                          title={t('Régénérer avec l’IA')}
                        >
                          ⟳ {t('Régénérer avec l’IA')}
                        </button>
                      )}
                    </div>
                    <div className="textdetails-charcount">
                      <span className="textdetails-charcount-badge">
                        {metaCharLimit != null
                          ? `${metaLength} / ${metaCharLimit} ${t('caractères')}`
                          : `${metaLength} ${t('caractères')}`}
                      </span>
                    </div>
                  </div>
                </div>
              )}


              {showUrlControls && showUrlSection && (
                <div className="textdetails-modal">
                  <div className="textdetails-field">
                    <label htmlFor="urlField">{t('URL cible :')}</label>
                    <input
                      id="urlField"
                      name="url"
                      type="text"
                      value={url || ''}
                      onChange={!isFieldDisabled('url') ? handleChange('url') : undefined}
                      placeholder={t("Entrez l'URL cible")}
                      disabled={isFieldDisabled('url')}
                    />
                  </div>
                </div>
              )}
            </>

          </div>
        </div>
      </div>
    </>
  );
};

export default TextDetailsIA;


